import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Button, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';
import { makeStyles } from '@material-ui/core/styles';
import { Chat } from '@material-ui/icons';

import useIsCompany from '../../../myHooks/useIsCompany';
import { algorithmCalculation } from '../../../redux/actions/Firebase';
import { fetchOwnedAgencyDataBySurveyId } from '../../../redux/actions/Data';
import { makeURL, findLatestExistingBatchData, batchDataSort } from '../../../helperFunctions';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';
import ChartCalculations from './chartCalculations';

import HeaderSelector from '../../../myComponents/headerSelector';
import Filters from '../../../myComponents/filters/Filters';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';
import ChatBot from '../../../myComponents/chatBot';

const useStyles = makeStyles(theme => ({
  chatButton: {
    display: 'flex',
    margin: '10px 0',
    padding: '7px 25px',
    borderRadius: '5px',
    alignItems: 'center',
    backgroundColor: '#AB8628',

    '&.MuiButton-root:hover.Mui-disabled': {
      backgroundColor: '#AB8628',
      color: '#fff !important',
    },

    '&.MuiButton-root.Mui-disabled': {
      backgroundColor: '#AB8628',
      color: '#fff !important',
    },

    '&:hover': {
      backgroundColor: '#967217',
    },

    '@media (max-width: 768px)': {
      padding: '5px 20px',
    },

    '@media (min-width: 1300px)': {
      margin: '15px 0',
      padding: '5px 25px',
    },
  },
  chatButtonText: {
    color: 'white',
    fontSize: '12px',
    margin: 0,

    '@media (max-width: 768px)': {
      fontSize: '10px',
    },

    '@media (min-width: 1300px)': {
      fontSize: '15px',
    },
  },
  chatIcon: {
    fontSize: '20px',
    color: 'white',
    marginRight: '7px',

    '@media (max-width: 768px)': {
      fontSize: '13px',
    },
  },
  headerSelectorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
    gridColumnGap: '40px',

    '@media (max-width: 768px)': {
      gridColumnGap: '30px',
    },
  },
}));

const BuyerDashboard = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const isCompany = useIsCompany();
  const appData = useSelector(({ data }) => data);
  const [buyerPersonaPickedId, setBuyerPersonaPickedId] = useState(null);
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);
  const [chosenFilters, setChosenFilters] = useState(null);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  const [chatOpened, setChatOpened] = useState(false);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  const [pickedBuyerPersona, setPickedBuyerPersona] = useState(null);

  useEffect(() => {
    if (!appData.loading && !appData.loadingAgencyData && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn();
      } else {
        if (!id) {
          history.push(makeURL('/buyer-personas/all'));
        } else {
          const pickedBuyerPersona = !appData.buyerPersonas ? false : appData.buyerPersonas.find(e => e.id === id);
          if (!pickedBuyerPersona) {
            if (!isCompany) return history.push(makeURL('/buyer-personas/all'));
            else {
              dispatch(fetchOwnedAgencyDataBySurveyId(id, 'buyer'));
              setUseEffectShouldTrigger(true);
            }
          } else {
            if (!pickedBatchData) {
              const { latestQuarter, latestYear } = findLatestExistingBatchData(
                appData.combinedBatchDataCodes[DEFAULT_BATCH_COUNTRY],
              );
              const pickedBatchToSet = {
                quarter: [latestQuarter],
                year: latestYear,
              };

              setPickedBatchData(pickedBatchToSet);
              setPickedBatchCountry(DEFAULT_BATCH_COUNTRY);

              personaChangeHandler(pickedBuyerPersona, pickedBatchToSet, DEFAULT_BATCH_COUNTRY);
            } else {
              personaChangeHandler(pickedBuyerPersona, pickedBatchData, pickedBatchCountry);
            }

            setPageIsLoading(false);
          }
        }
      }
    }
  }, [appData.loading, appData.loadingAgencyData, id]);

  useEffect(() => {
    if (buyerPersonaPickedId) {
      history.push(makeURL(`/buyer-personas/${buyerPersonaPickedId}`));
      setUseEffectShouldTrigger(true);
    }
  }, [buyerPersonaPickedId]);

  const calculateHandler = (pickedPersona, filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    algorithmCalculation(
      'Buyer',
      {
        persona: pickedPersona,
        filters: filters,
        batches: newPickedBatchData,
        batchCountry: newPickedBatchCountry,
      },
      dispatch,
      appData.visibleAgencies,
    )
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn();
      });
  };

  const personaChangeHandler = (pickedPersona, newPickedBatchData, newPickedBatchCountry) => {
    setPickedBuyerPersona(pickedPersona);
    calculateHandler(pickedPersona, chosenFilters, newPickedBatchData, newPickedBatchCountry);
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(pickedBuyerPersona, preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run buyer persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBuyerPersona, chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestBatchYear = Object.keys(appData.combinedBatchDataCodes[value])
      .sort()
      .pop();
    const quarters = appData.combinedBatchDataCodes[value][newestBatchYear].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: newestBatchYear,
    };

    setPickedBatchData(batchInfoToSet);
    setPickedBatchCountry(value);

    //Run brand persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBuyerPersona, chosenFilters, batchInfoToSet, value);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.combinedBatchDataCodes[pickedBatchCountry][value].sort(batchDataSort);
    const latestQuarter = quarters[quarters.length - 1];

    const batchInfoToSet = {
      quarter: [latestQuarter],
      year: value,
    };

    setPickedBatchData(batchInfoToSet);

    //Run buyer persona algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBuyerPersona, chosenFilters, batchInfoToSet, pickedBatchCountry);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
      {calculations.results ? (
        <div>
          <UserDropdownHeader />
          {pickedBuyerPersona && (
            <ChatBot pickedPersona={pickedBuyerPersona} chatOpened={chatOpened} setChatOpened={setChatOpened} type="buyer" />
          )}

          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff', gap: '30px' }}>
            <Box flex="1">
              <Box className={classes.headerSelectorContainer}>
                <HeaderSelector
                  selectedId={buyerPersonaPickedId ? buyerPersonaPickedId : id}
                  setPickedId={setBuyerPersonaPickedId}
                  data={appData}
                  type="buyer"
                  redirect={true}
                  bgColor="#AB8628"
                />
                <Button onClick={() => setChatOpened(!chatOpened)} className={classes.chatButton}>
                  <Chat className={classes.chatIcon} />
                  <Typography component="div" variant="button" className={classes.chatButtonText}>
                    CHAT
                  </Typography>
                </Button>
              </Box>
            </Box>

            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <BuyerBatchSelector
                calculationType={'batch'}
                onChangeCalculationType={() => {}}
                pickedBuyerPersona={null}
                onChangeBuyerPersona={() => {}}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                type="buyer"
                withConsumerHeading
              />
            </Box>
          </Box>

          <Filters calculateHandler={onFiltersSubmit} themeColor="yellow" pickedBatchCountry={pickedBatchCountry} pickedBatchData={pickedBatchData} />

          {calculations.results.no_data ? (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
              <Typography
                variant="h6"
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  margin: '0',
                  padding: '0',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                No data, choose other "Consumer Settings" combination.
              </Typography>
            </Box>
          ) : (
            <ChartCalculations type="brand" data={calculations.results} persona={pickedBuyerPersona} />
          )}
        </div>
      ) : null}
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default BuyerDashboard;
